import { FC, useEffect, useMemo, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Tab, Tabs, TextField } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import CustomDataGrid from "../DataGrid/DataGrid";
import InventoryEmptyState from "./InventoryEmptyState";
import LocationSelect from "./pickers/LocationSelect";
import SkuSelect from "./pickers/SkuSelect";
import BatchSelect from "./pickers/BatchSelect";
import { FilterBar, FilterSearch } from "./InvFilters";
import Filters from "../Filters/Filters";
import { useUnitName, unitOptionTokens, factorOf, baseUnitNameOf } from "./pickers/useUnitName";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchStockLevels, fetchStockMovements, adjustStock } from "../../store/stockStore/stockThunk";
import { ApiStatus, defaultRowsPerPageOptions } from "../../utils/api.types";
import { movementReasonLabel } from "../../utils/inventoryConstants";
import { Utils } from "../../utils/common-utils";

interface StockViewProps {
  // Item view page embeds this view locked to one SKU: the item filter and the
  // adjust-stock button are hidden and every fetch carries that skuId.
  fixedSkuId?: string;
  embedded?: boolean;
}

const StockView: FC<StockViewProps> = ({ fixedSkuId, embedded }) => {
  const dispatch = useAppDispatch();
  const { levels, levelsRowCount, movements, movementsRowCount, status } = useAppSelector((state) => state.stock);

  const unitName = useUnitName();

  const [tab, setTab] = useState(0); // 0 = on-hand, 1 = movements
  const [locationId, setLocationId] = useState("");
  const [skuId, setSkuId] = useState(fixedSkuId || "");

  // Keep the lock in sync when the embedding page navigates to another item.
  useEffect(() => {
    if (fixedSkuId) { setSkuId(fixedSkuId); setPage(0); }
  }, [fixedSkuId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Batch-number search (On-Hand only; resolved server-side against batch number).
  const [batchNumber, setBatchNumber] = useState("");
  const [debouncedBatch, setDebouncedBatch] = useState("");
  // Expiry range filter (On-Hand only; resolved server-side against batch expiry).
  const [expiryFrom, setExpiryFrom] = useState("");
  const [expiryTo, setExpiryTo] = useState("");
  // Bumped on reset to remount the date-range dropdown back to "Select Date".
  const [filtersKey, setFiltersKey] = useState(0);

  // Server-side pagination (0-based page, matching the Items list / backend skip).
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // adjust-stock dialog state
  const [adjOpen, setAdjOpen] = useState(false);
  const [aSku, setASku] = useState<any>(null);
  const [aLocation, setALocation] = useState("");
  const [aBatch, setABatch] = useState("");
  const [aMode, setAMode] = useState<"set" | "delta">("delta");
  const [aQty, setAQty] = useState("");
  const [aUnit, setAUnit] = useState("");
  const [aNote, setANote] = useState("");
  const [aSaving, setASaving] = useState(false);

  const buildParams = () => {
    const p: string[] = [];
    if (locationId) p.push(`locationId=${locationId}`);
    if (skuId) p.push(`skuId=${skuId}`);
    // Batch search + expiry range apply to On-Hand stock levels only.
    if (tab === 0) {
      if (debouncedBatch) p.push(`batchNumber=${encodeURIComponent(debouncedBatch)}`);
      if (expiryFrom) p.push(`expiryFrom=${expiryFrom}`);
      if (expiryTo) p.push(`expiryTo=${expiryTo}`);
    }
    p.push(`page=${page}`);
    p.push(`limit=${pageSize}`);
    return p.join("&");
  };

  const loadData = () => {
    const params = buildParams();
    if (tab === 0) dispatch(fetchStockLevels(params));
    else dispatch(fetchStockMovements(params));
  };

  // Debounce only the free-text batch search so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedBatch(batchNumber); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [batchNumber]);

  useEffect(() => {
    loadData();
  }, [dispatch, tab, locationId, skuId, debouncedBatch, expiryFrom, expiryTo, page, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switching tab / filter / page size jumps back to the first page. Bundled with
  // the triggering setState so React batches them into a single re-render (one fetch).
  const handleTabChange = (v: number) => { setTab(v); setPage(0); };
  const handleLocationChange = (v: string) => { setLocationId(v); setPage(0); };
  const handleSkuChange = (id: string) => { setSkuId(id); setPage(0); };
  // Expiry range comes from the shared Filters date-range dropdown. It emits a
  // query string like "start=YYYY-MM-DD&end=YYYY-MM-DD&voucherType=all"; we read
  // ONLY start/end and map them to expiryFrom/expiryTo (the rest is ignored, so
  // nothing extra reaches the stock API).
  //
  // Filters calls this from inside a setTimeout, so the state updates aren't
  // auto-batched — updating from then to separately would fire TWO fetches (the
  // first with only expiryFrom). Batch them so a single, complete fetch goes out.
  const handleExpiryDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    const from = params.get("start") || "";
    const to = params.get("end") || "";
    unstable_batchedUpdates(() => {
      setExpiryFrom(from);
      setExpiryTo(to);
      setPage(0);
    });
  };
  const handlePageSizeChange = (n: number) => { setPageSize(n); setPage(0); };
  const resetFilters = () => {
    setLocationId("");
    setSkuId(fixedSkuId || "");
    setBatchNumber("");
    setExpiryFrom("");
    setExpiryTo("");
    // Remount the date-range dropdown so it reinitialises back to "Select Date"
    // (Filters keeps its own internal selection; it isn't a controlled input).
    setFiltersKey((k) => k + 1);
    setPage(0);
  };

  // Base units contained in 1 of the chosen unit. > 1 means a larger pack unit
  // (e.g. 1 BOTTLE = 750 MILLILITRE), which is what we spell out for the user.
  const aFactor = factorOf(aSku, aUnit);
  const aBaseUnitName = baseUnitNameOf(aSku, unitName);
  const showConversion = !!aSku && !!aUnit && aFactor > 1;
  const aQtyNum = Number(aQty);
  const hasQty = aQty !== "" && !Number.isNaN(aQtyNum) && aQtyNum !== 0;

  const onAdjust = async () => {
    if (!aSku?._id || !aLocation || aQty === "") {
      toast.error("Item, location and quantity are required");
      return;
    }
    setASaving(true);
    try {
      await dispatch(
        adjustStock({
          skuId: aSku._id,
          locationId: aLocation,
          batchId: aBatch || null,
          mode: aMode,
          qty: Number(aQty),
          unit: aUnit || null,
          note: aNote || null,
        })
      ).unwrap();
      toast.success("Stock adjusted");
      setAdjOpen(false);
      setAQty("");
      setANote("");
      loadData();
    } catch (e: any) {
      toast.error(e?.message || "Adjustment failed");
    } finally {
      setASaving(false);
    }
  };

  // Expiry cell: red when on/after today (so near/over-expiry batches stand out).
  const expiryCell = (d: any) => {
    if (!d) return <span>—</span>;
    const dt = new Date(d);
    const overdue = dt.getTime() <= Date.now();
    return <span style={{ color: overdue ? "#c33b3b" : "inherit", fontWeight: overdue ? 600 : 400 }}>{dt.toLocaleDateString()}</span>;
  };

  const levelCols: GridColDef[] = useMemo(
    () => [
      { field: "sku", headerName: "Item", flex: 1.4, minWidth: 150, valueGetter: (p: GridValueGetterParams) => p.row.skuId?.name || "" },
      { field: "location", headerName: "Location", flex: 1, minWidth: 120, valueGetter: (p: GridValueGetterParams) => p.row.locationId?.name || "" },
      { field: "batch", headerName: "Batch", flex: 1, minWidth: 110, valueGetter: (p: GridValueGetterParams) => p.row.batchId?.batchNumber || "—" },
      { field: "expiry", headerName: "Expiry", flex: 0.9, minWidth: 110, sortable: false, renderCell: ({ row }: GridValueGetterParams) => expiryCell(row.batchId?.expiryDate) },
      { field: "quantity", headerName: "Qty (base)", flex: 0.8, minWidth: 100, valueGetter: (p: GridValueGetterParams) => `${p.row.quantity ?? 0} ${p.row.skuId?.baseUnitName || ""}` },
      { field: "avgPurchaseCost", headerName: "Avg Purchase Cost (₹)", flex: 0.8, minWidth: 130, valueGetter: (p: GridValueGetterParams) => Utils.formatNumber(p.row.avgPurchaseCost ?? 0, false, true) },
      { field: "reserved", headerName: "Reserved", flex: 0.7, minWidth: 90, valueGetter: (p: GridValueGetterParams) => p.row.reserved ?? 0 },
    ],
    []
  );

  const movementCols: GridColDef[] = useMemo(
    () => [
      { field: "createdAt", headerName: "Date", flex: 1, minWidth: 130, sortable: false, renderCell: ({ row }: GridValueGetterParams) => {
          if (!row.createdAt) return <span>—</span>;
          const d = new Date(row.createdAt);
          return (
            <Box sx={{ lineHeight: 1.2 }}>
              <div>{Utils.formatDate(row.createdAt)}</div>
              <div style={{ fontSize: 11, color: "#7a8290" }}>{d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })}</div>
            </Box>
          );
        } },
      { field: "sku", headerName: "Item", flex: 1.2, minWidth: 140, valueGetter: (p: GridValueGetterParams) => p.row.skuId?.name || "" },
      { field: "location", headerName: "Location", flex: 1, minWidth: 110, valueGetter: (p: GridValueGetterParams) => p.row.locationId?.name || "" },
      // Batch + expiry combined; a red "Expired" chip when the batch expiry has passed.
      { field: "batch", headerName: "Batch / Expiry", flex: 1.1, minWidth: 150, sortable: false, renderCell: ({ row }: GridValueGetterParams) => {
          const batchNo = row.batchId?.batchNumber;
          const exp = row.batchId?.expiryDate;
          if (!batchNo && !exp) return <span>—</span>;
          const expired = exp ? new Date(exp).getTime() <= Date.now() : false;
          return (
            <Box sx={{ lineHeight: 1.3, py: 0.3 }}>
              <div>{batchNo || "—"}</div>
              {exp && (expired ? (
                <Chip size="small" style={{ borderRadius: 7, color: "#c33b3b", backgroundColor: "#fbecec", height: 18, fontSize: 10 }} label={`Expired ${Utils.formatDate(exp)}`} />
              ) : (
                <span style={{ fontSize: 11, color: "#7a8290" }}>Exp {Utils.formatDate(exp)}</span>
              ))}
            </Box>
          );
        } },
      { field: "reason", headerName: "Reason", flex: 1.1, minWidth: 150, sortable: false, renderCell: ({ row }: GridValueGetterParams) => {
          // For transfer movements, show whether it was a direct transfer or one
          // raised from a requisition (source comes from the movements endpoint).
          const isTransfer = row.refType === "StockTransfer";
          const src = row.source;
          const sub = isTransfer
            ? src?.fromRequisition
              ? `via ${src.reqNumber || "Requisition"}`
              : src?.transferNumber
              ? `Direct · ${src.transferNumber}`
              : "Direct"
            : null;
          return (
            <Box sx={{ lineHeight: 1.2 }}>
              <div>{movementReasonLabel(row.reason)}</div>
              {sub && <div style={{ fontSize: 11, color: src?.fromRequisition ? "#1f4e8a" : "#7a8290" }}>{sub}</div>}
            </Box>
          );
        } },
      // What the user actually entered (e.g. "+5 box"). Falls back to base units for
      // movements that arrived pre-converted (e.g. POS sale depletions).
      { field: "entered", headerName: "Entered", flex: 0.8, minWidth: 110, sortable: false, renderCell: ({ row }: GridValueGetterParams) => {
          const hasEntry = row.entryQty != null && row.entryUnit;
          const qty = hasEntry ? row.entryQty : row.qtyDelta;
          const unit = hasEntry ? unitName(row.entryUnit) : (row.skuId?.baseUnitName || "base");
          return (
            <span style={{ color: qty < 0 ? "#c33b3b" : "#49a563" }} title={hasEntry ? "" : "Recorded in base units"}>
              {qty > 0 ? `+${qty}` : qty} {unit}{hasEntry ? "" : " *"}
            </span>
          );
        } },
      { field: "qtyDelta", headerName: "Qty Δ (base)", flex: 0.7, minWidth: 100, renderCell: ({ row }: GridValueGetterParams) => (
          <span style={{ color: row.qtyDelta < 0 ? "#c33b3b" : "#49a563" }}>{row.qtyDelta > 0 ? `+${row.qtyDelta}` : row.qtyDelta} {row.skuId?.baseUnitName || ""}</span>
        ) },
      { field: "balanceAfter", headerName: "Balance", flex: 0.8, minWidth: 110, sortable: false, renderCell: ({ row }: GridValueGetterParams) => {
          const bal = row.balanceAfter ?? 0;
          if (bal === 0) {
            return <Chip size="small" style={{ borderRadius: 7, color: "#c33b3b", backgroundColor: "#fbecec", height: 20, fontSize: 11 }} label="Empty (0)" />;
          }
          return <span>{bal} {row.skuId?.baseUnitName || ""}</span>;
        } },
      { field: "flaggedOversell", headerName: "Flag", flex: 0.6, minWidth: 80, renderCell: ({ row }: GridValueGetterParams) => (row.flaggedOversell ? <Chip size="small" label="Oversell" sx={{ color: "#c33b3b" }} /> : null) },
    ],
    [unitName]
  );

  const rows = tab === 0 ? levels : movements;
  const columns = tab === 0 ? levelCols : movementCols;
  const rowCount = tab === 0 ? levelsRowCount : movementsRowCount;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Tabs value={tab} onChange={(_e, v) => handleTabChange(v)}>
          <Tab label="On-Hand" sx={{ textTransform: "none" }} />
          <Tab label="Movements" sx={{ textTransform: "none" }} />
        </Tabs>
        {!embedded && (
          <Button startIcon={<TuneIcon />} size="small" variant="outlined" onClick={() => setAdjOpen(true)}>
            Adjust stock
          </Button>
        )}
      </Box>

      <FilterBar onReset={resetFilters}>
        <LocationSelect borderless width={220} value={locationId} onChange={handleLocationChange} label="Filter by location" activeOnly={false} />
        {!fixedSkuId && (
          <SkuSelect borderless width={260} value={skuId} onChange={(sku) => handleSkuChange(sku?._id || "")} label="Filter by item" />
        )}
        {tab === 0 && (
          <>
            <FilterSearch value={batchNumber} onChange={setBatchNumber} placeholder="Search batch" width={170} />
            {/* The Filters dropdown already has its own rounded border; just notch
                the "Expiry date" label onto that existing border (no extra box). */}
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <span style={{ position: "absolute", top: -8, left: 20, padding: "0 5px", background: "#fff", fontSize: 12, color: "#6b7280", lineHeight: 1, zIndex: 1, pointerEvents: "none" }}>Expiry date</span>
              <Filters key={filtersKey} type="inventory-stock" dateOnly hidePartySearch hideFilter hideReset filterHandler={handleExpiryDateFilter} />
            </div>
          </>
        )}
      </FilterBar>

      {status !== ApiStatus.LOADING && rows.length === 0 ? (
        tab === 0 ? (
          <InventoryEmptyState
            height="75vh"
            icon={<Inventory2OutlinedIcon />}
            title="No Stock On-Hand"
            description="No stock matches the current filters. Receive goods or adjust stock to start tracking on-hand quantities."
            actionLabel={embedded ? undefined : "Adjust stock"}
            onAction={embedded ? undefined : () => setAdjOpen(true)}
            caption="Need help? Check the Guide tab on inventory management"
          />
        ) : (
          <InventoryEmptyState
            height="75vh"
            icon={<HistoryOutlinedIcon />}
            title="No Stock Movements"
            description="Stock movements will appear here as you receive, transfer, adjust, or sell items."
            caption="Need help? Check the Guide tab on inventory management"
          />
        )
      ) : (
        <div style={{ height: "75vh", width: "100%" }}>
          <CustomDataGrid
            columns={columns}
            rows={rows}
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(p: number) => setPage(p)}
            onPageSizeChange={handlePageSizeChange}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            getRowId={(row: any) => row._id || Math.random().toString()}
            loading={status === ApiStatus.LOADING}
          />
        </div>
      )}

      <Dialog open={adjOpen} onClose={() => setAdjOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Adjust stock</DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: 13, color: "#5b6472", mb: 1.5 }}>
            Corrects on-hand by posting an <b>adjustment</b> entry (the ledger and current stock update automatically).
          </Box>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <SkuSelect
                value={aSku?._id}
                onChange={(sku) => {
                  setASku(sku);
                  setAUnit(sku ? unitOptionTokens(sku)[0] || "" : "");
                  setABatch("");
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocationSelect value={aLocation} onChange={setALocation} activeOnly={false} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <BatchSelect skuId={aSku?._id} locationId={aLocation} value={aBatch} onChange={setABatch} />
            </Grid>
            {/* Mode + Quantity share one grid item so their hint sits on the
                same line as the Unit field's conversion helper text. */}
            <Grid item xs={12} sm={8}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField select fullWidth size="small" label="Mode" value={aMode} onChange={(e) => setAMode(e.target.value as any)}>
                  <MenuItem value="delta">Adjust by (+/−)</MenuItem>
                  <MenuItem value="set" disabled>Set on-hand to</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Quantity"
                  value={aQty}
                  onChange={(e) => setAQty(e.target.value)}
                />
              </Box>
              {/* Matches MUI FormHelperText metrics so it lines up with the one
                  under the Unit field. */}
              <Box sx={{ fontSize: "0.75rem", color: "#9da7b7", lineHeight: 1.66, mt: "3px", mx: "14px" }}>
                {aMode === "delta"
                  ? "Enter a positive quantity to add stock (e.g. 5), or a negative one to reduce it (e.g. -5)."
                  : "Enter the new on-hand quantity."}
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              {/* Conversion sits under the Unit field, and only for a pack unit
                  above the base unit — spells out the item's saved conversion
                  and, once a quantity is typed, the base-unit total. */}
              <TextField
                select
                fullWidth
                size="small"
                label="Unit"
                value={aUnit}
                onChange={(e) => setAUnit(e.target.value)}
                helperText={showConversion ? (
                  <>
                    1 {unitName(aUnit)} = {aFactor} {aBaseUnitName}
                    {hasQty && (
                      <span style={{ color: "#292d35", display: "block" }}>
                        {aQtyNum} {unitName(aUnit)} = {aQtyNum * aFactor} {aBaseUnitName}
                        {aMode === "delta" && aQtyNum < 0 ? " (removed)" : ""}
                      </span>
                    )}
                  </>
                ) : undefined}
              >
                {unitOptionTokens(aSku).map((u) => (
                  <MenuItem key={u} value={u}>{unitName(u)}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reason / note" value={aNote} onChange={(e) => setANote(e.target.value)} placeholder="e.g. recount, breakage" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onAdjust} disabled={aSaving || !aSku?._id || !aLocation || aQty === ""}>
            Apply adjustment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockView;
